import React from 'react';
import moment, { type Moment } from 'moment';
import { FormControl, InputLabel, MenuItem, Select, Typography, type SelectChangeEvent } from '@mui/material';

import useSettings from '../../hooks/useSettings';

export interface DateRangePresetType {
	id: string;
	label: string;
	range: {
		from: Moment;
		to: Moment;
	};
}

interface DateRangePresetSelectProps {
	value?: DateRangePresetType;
	sendChange: (newPresetRange?: DateRangePresetType) => void;
}

const presets: DateRangePresetType[] = [
	{
		id: 'lastSevenDays',
		label: 'DATERANGE_PRESET_LASTSEVENDAYS',
		range: {
			from: moment().subtract(7, 'day').startOf('day'),
			to: moment().subtract(1, 'day').endOf('day')
		}
	},
	{
		id: 'nextSevenDays',
		label: 'DATERANGE_PRESET_NEXTSEVENDAYS',
		range: {
			from: moment().startOf('day'),
			to: moment().add(7, 'day').endOf('day')
		}
	},
	{
		id: 'lastWeek',
		label: 'DATERANGE_PRESET_LASTWEEK',
		range: {
			from: moment().subtract(1, 'week').startOf('week'),
			to: moment().subtract(1, 'week').endOf('week')
		}
	},
	{
		id: 'currentWeek',
		label: 'DATERANGE_PRESET_CURRENTWEEK',
		range: {
			from: moment().startOf('week'),
			to: moment().endOf('week')
		}
	},
	{
		id: 'nextWeek',
		label: 'DATERANGE_PRESET_NEXTWEEK',
		range: {
			from: moment().add(1, 'week').startOf('week'),
			to: moment().add(1, 'week').endOf('week')
		}
	},
	{
		id: 'lastMonth',
		label: 'DATERANGE_PRESET_LASTMONTH',
		range: {
			from: moment().subtract(1, 'month').startOf('month'),
			to: moment().subtract(1, 'month').endOf('month')
		}
	},
	{
		id: 'currentMonth',
		label: 'DATERANGE_PRESET_CURRENTMONTH',
		range: {
			from: moment().startOf('month'),
			to: moment().endOf('month')
		}
	},
	{
		id: 'nextMonth',
		label: 'DATERANGE_PRESET_NEXTMONTH',
		range: {
			from: moment().add(1, 'month').startOf('month'),
			to: moment().add(1, 'month').endOf('month')
		}
	},
	{
		id: 'lastYear',
		label: 'DATERANGE_PRESET_LASTYEAR',
		range: {
			from: moment().subtract(1, 'year').startOf('year'),
			to: moment().subtract(1, 'year').endOf('year')
		}
	},
	{
		id: 'currentYear',
		label: 'DATERANGE_PRESET_CURRENTYEAR',
		range: {
			from: moment().startOf('year'),
			to: moment().endOf('year')
		}
	},
	{
		id: 'nextYear',
		label: 'DATERANGE_PRESET_NEXTYEAR',
		range: {
			from: moment().add(1, 'year').startOf('year'),
			to: moment().add(1, 'year').endOf('year')
		}
	}
];

function DateRangePresetSelect(props: DateRangePresetSelectProps) {
	const { t } = useSettings();

	function handlePresetRangeChange(event: SelectChangeEvent) {
		const newPresetRange = presets.find(preset => preset.id === event.target.value);
		props.sendChange(newPresetRange);
	}

	return (
		<FormControl fullWidth>
			<InputLabel id="rangePresetSelectLabel">
				<Typography variant="subtitle2">{t('DATERANGE_FILTER_PRESET')}</Typography>
			</InputLabel>
			<Select
				labelId="rangePresetSelectLabel"
				label={t('DATERANGE_FILTER_PRESET')}
				size="small"
				value={props.value?.id || 'custom'}
				onChange={handlePresetRangeChange}
			>
				<MenuItem value="custom">
					<i>{t('DATERANGE_PRESET_CUSTOM')}</i>
				</MenuItem>
				{presets.map((shortcut, index) => (
					<MenuItem key={index} value={shortcut.id}>
						{t(shortcut.label)}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}

export default React.memo(DateRangePresetSelect);
